import React, { useEffect, useRef, useState } from 'react';
import { previousRecapDateLabel } from './previousRecapsModel';
import './PreviousRecaps.css';

/** One read-only tier block, inside "Went home to the family" (2026-09-25),
 *  the same grouping as today's editors above. Teachers only no longer reaches
 *  this panel (pass 4 P5.1, 2026-10-05): previousRecapsModel drops it, and
 *  the Private file holds that history. */
const renderTier = (tier) => (
	<div key={tier.id} className={`ld-prevrecap__tier${tier.privateTier ? ' ld-prevrecap__tier--private' : ''}`}>
		<div className="ld-prevrecap__tierlabel">
			<i className={`fa-sharp fa-solid ${tier.icon}`} aria-hidden="true" /> {tier.title}
		</div>
		<p className="ld-prevrecap__text">{tier.text}</p>
	</div>
);

/**
 * "Previous recaps": the read-only panel inside the one page-owned RecapDrawer
 * (2026-09-21). The last four lessons for this student, newest first, the
 * family tiers (Everyone, Parents), any teacher: date, teacher, tier label,
 * the text. The Teachers only tier left this panel on 2026-10-05 (P5.1).
 *
 * READ-ONLY BY CONSTRUCTION. Nothing here mounts an editor. The live editors
 * for today's three tiers sit above it in the same drawer; a prior lesson's
 * recap is history, and editing history from inside a different lesson is the
 * thing that would let a note about last Tuesday quietly become a note about
 * today.
 *
 * A DISCLOSURE, CLOSED BY DEFAULT, ALWAYS VISIBLE. The drawer is wrap-time
 * writing space and four lessons of text above the Back button would push
 * today's editors around; the toggle stays rendered at every width so it is
 * never a hover-only affordance (CLAUDE.md). The count in the label says
 * whether opening it is worth a tap before it is tapped.
 *
 * Copy: no em dashes (Anthony, 2026-09-16); icons are FontAwesome only.
 */
const PreviousRecaps = ({ lessons = [], isLoading = false, error = '', defaultOpen = false, scrollIntoViewOnMount = false }) => {
	const [open, setOpen] = useState(defaultOpen);
	const rootRef = useRef(null);

	// The Last lesson row opens the drawer AT this panel (design pass 2): the
	// sheet's own scroll region is what moves, synchronously after commit so a
	// hidden tab (no animation frames) still lands here. Guarded: jsdom has no
	// scrollIntoView.
	useEffect(() => {
		if (!scrollIntoViewOnMount) return;
		const el = rootRef.current;
		if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
	}, [scrollIntoViewOnMount]);
	const count = lessons.length;
	// "(n)" rather than "n lessons" (design pass 2, change 9): the row already
	// says "Previous recaps", and a bare count is what the header has room for
	// beside the chevron in a 520px drawer and a 375px sheet alike.
	const summary = isLoading ? 'Loading' : `(${count})`;

	return (
		<section ref={rootRef} className="ld-prevrecaps" aria-labelledby="ld-prevrecaps-title">
			<button
				type="button"
				className="ld-prevrecaps__toggle"
				aria-expanded={open}
				aria-controls="ld-prevrecaps-body"
				onClick={() => setOpen((v) => !v)}>
				<i className="fa-sharp fa-solid fa-clock-rotate-left" aria-hidden="true" />
				<span id="ld-prevrecaps-title" className="ld-prevrecaps__title">Previous recaps</span>
				<span className="ld-prevrecaps__count">{summary}</span>
				<i
					className={`fa-sharp fa-solid ${open ? 'fa-chevron-up' : 'fa-chevron-down'} ld-prevrecaps__chev`}
					aria-hidden="true"
				/>
			</button>

			{open && (
				<div id="ld-prevrecaps-body" className="ld-prevrecaps__body">
					{error && <p className="ld-prevrecaps__empty">{error}</p>}
					{!error && !isLoading && count === 0 && (
						<p className="ld-prevrecaps__empty">There are no earlier recaps for this student yet.</p>
					)}
					{!error && isLoading && <p className="ld-prevrecaps__empty">Loading earlier recaps.</p>}
					{!error &&
						lessons.map((lesson) => (
							<article key={lesson.key} className="ld-prevrecap">
								<header className="ld-prevrecap__head">
									<span className="ld-prevrecap__date">{previousRecapDateLabel(lesson.date)}</span>
									<span className="ld-prevrecap__teacher">
										<i className="fa-sharp fa-solid fa-user" aria-hidden="true" /> {lesson.teacherName}
									</span>
								</header>
								<div className="ld-prevrecap__home">
									<div className="ld-prevrecap__homelabel">
										<i className="fa-sharp fa-solid fa-house" aria-hidden="true" /> Went home to the family
									</div>
									{lesson.tiers.map(renderTier)}
								</div>
							</article>
						))}
				</div>
			)}
		</section>
	);
};

export default PreviousRecaps;
